/**
 * Stage-Aware Prediction Adjustments
 *
 * Adjusts Monte Carlo simulation parameters based on live session data
 * (FP1/FP2/FP3/Sprint Quali/Sprint/Qualifying positions).
 *
 * As more practice sessions complete, the expected qualifying position
 * becomes more informed and variance shrinks, improving prediction accuracy.
 */

import type { TeamCalculatorDriver } from "../algorithms/types";
import type { DriverSimParams } from "./types";

// ============================================================================
// Correlation Constants (reused from live-prediction-engine.ts)
// ============================================================================

/** How strongly each FP session correlates with qualifying position.
 * FP1: Experimental setups, least representative.
 * FP2: Often race simulation (long runs on high fuel) — positions less
 *       representative of qualifying pace than FP1/FP3 short runs.
 * FP3: Final practice, most representative — closest to qualifying sim. */
const FP_WEIGHT = {
  fp1: 0.5,
  fp2: 0.6,
  fp3: 0.8,
} as const;

/** How much variance reduction each weight level provides */
const VARIANCE_REDUCTION_FACTOR = 0.55;

/** Sprint quali → GP quali correlation.
 * Sprint quali is similar to GP quali but shorter (SQ1/SQ2/SQ3 vs Q1/Q2/Q3).
 * Treated as the lock stage for sprint weekends — same weight as FP3 for regular weekends. */
const SPRINT_QUALI_WEIGHT = 0.8;

/** Sprint race → GP quali correlation.
 * Sprint race result is less indicative of qualifying pace since
 * it involves race craft (starts, strategy, tire deg) not just one-lap pace. */
const SPRINT_RACE_WEIGHT = 0.5;

/** After actual qualifying: near-certain knowledge */
const QUALI_OVERRIDE_STD_DEV = 0.5;

/**
 * When live session data shows a driver qualifying BETTER than their historical
 * average, their race delta (positions gained) should be dampened. A driver who
 * historically qualified P8 and recovered to P5 (delta +3) shouldn't ALSO recover
 * +3 from an improved P4 qualifying — they're already near the front.
 *
 * Early stages (FP1/FP2): 0.6 — less certain about qualifying position,
 * so allow more of the historical recovery pattern to persist.
 * Lock stages and beyond: 0.75 — session data is more definitive,
 * so more of the qualifying improvement explains the recovery.
 */
const DELTA_DAMPING_EARLY = 0.6;
const DELTA_DAMPING_LATE = 0.75;

/** Lock stages and beyond get stronger damping */
const LATE_STAGES = new Set([
  "after_fp3",
  "after_sprint_quali",
  "after_sprint",
  "after_quali",
]);

function getDeltaDampingFactor(stage: string): number {
  return LATE_STAGES.has(stage) ? DELTA_DAMPING_LATE : DELTA_DAMPING_EARLY;
}

/**
 * Minimum expected race position after applying delta. No driver can consistently
 * finish better than P1.5 (allows for occasional P1 simulation draws but prevents
 * the mean from being unrealistically low).
 */
const MIN_EXPECTED_RACE_POSITION = 1.5;

/**
 * Reference qualifying std dev for consistency scaling.
 * Drivers with qualiStdDev below this get reduced FP influence,
 * because noisy FP positions shouldn't override a strong historical pattern
 * (e.g., VER consistently qualifying P1-3 shouldn't be pulled to P5 by one FP3 result).
 */
const CONSISTENCY_REFERENCE_STD_DEV = 3.0;

// ============================================================================
// Core Adjustment Functions
// ============================================================================

/**
 * Compute how much FP/session positions should influence a driver's prediction.
 * Consistent qualifiers (low stdDev) are less affected by noisy FP positions.
 * Returns a factor in [0.5, 1.0] that scales the session weight.
 */
function getConsistencyFactor(qualiStdDev: number): number {
  // Floor at 0.35 so FP data always has meaningful influence even for very consistent qualifiers
  return Math.max(
    0.35,
    Math.min(1.0, qualiStdDev / CONSISTENCY_REFERENCE_STD_DEV),
  );
}

/**
 * Blend a session position with the historical expected qualifying position.
 */
function blendExpectedQuali(
  baseExpectedQuali: number,
  sessionPosition: number,
  weight: number,
): number {
  return baseExpectedQuali * (1 - weight) + sessionPosition * weight;
}

/**
 * Reduce qualifying standard deviation based on session weight.
 * More data → less uncertainty.
 */
function reduceQualiStdDev(baseStdDev: number, weight: number): number {
  return baseStdDev * (1 - weight * VARIANCE_REDUCTION_FACTOR);
}

// ============================================================================
// Main Entry Point
// ============================================================================

/**
 * Adjust driver simulation parameters based on the current weekend stage.
 *
 * At pre_weekend: returns params unchanged (no session data).
 * At each subsequent stage: blends session position data with the historical
 * estimate, progressively narrowing the variance.
 *
 * Null positions at the current stage mean "driver got no time" (crash, mechanical, DNS)
 * and are treated as P20 — a strong negative signal. We do NOT fall back to prior sessions
 * for the current stage's data, because the session happened and null means last place.
 */
export function adjustParamsForStage(
  params: DriverSimParams,
  driver: TeamCalculatorDriver,
  currentStage: string,
): DriverSimParams {
  if (currentStage === "pre_weekend" || currentStage === "after_race") {
    return params;
  }

  // Clone to avoid mutating the original
  const adjusted: DriverSimParams = { ...params };
  const baseQuali = params.expectedQuali;

  // Consistency factor: consistent qualifiers (low qualiStdDev) are less
  // affected by noisy FP positions. A driver who always qualifies P1-3
  // shouldn't be pulled to P5+ by one FP3 outlier.
  const cf = getConsistencyFactor(params.qualiStdDev);

  // Determine what session data is available
  const fp1 = driver.fp1Position;
  const fp2 = driver.fp2Position;
  const fp3 = driver.fp3Position;
  const sprintQuali = driver.sprintQualiPosition;
  const sprint = driver.sprintPosition;
  const quali = driver.qualiPosition;

  switch (currentStage) {
    case "after_fp1": {
      // fp1=null means no time in FP1 (crash, mechanical, DNS) → treat as P20
      const effectiveFp1 = fp1 ?? 20;
      const w = FP_WEIGHT.fp1 * cf;
      adjusted.expectedQuali = blendExpectedQuali(
        params.expectedQuali,
        effectiveFp1,
        w,
      );
      adjusted.qualiStdDev = reduceQualiStdDev(params.qualiStdDev, w);
      break;
    }

    case "after_fp2": {
      // FP2 happened — null means no time → P20 (NOT a fallback to FP1).
      // FP1 is a prior session: use actual if available, else use FP2 value (both penalised or both good).
      // Weighted blend: FP1 (qualifying sim) higher than FP2 (race sim).
      const effectiveFp2 = fp2 ?? 20;
      const effectiveFp1ForBlend = fp1 ?? effectiveFp2;
      const avgPos = effectiveFp1ForBlend * 0.6 + effectiveFp2 * 0.4;
      const w = FP_WEIGHT.fp2 * cf;
      adjusted.expectedQuali = blendExpectedQuali(
        params.expectedQuali,
        avgPos,
        w,
      );
      adjusted.qualiStdDev = reduceQualiStdDev(params.qualiStdDev, w);
      break;
    }

    case "after_fp3": {
      // FP3 is the LOCK STAGE for regular weekends — most representative session.
      // fp3=null means no time in FP3 → P20 (NOT a fallback to FP2).
      const effectiveFp3 = fp3 ?? 20;
      const w = FP_WEIGHT.fp3 * cf;
      adjusted.expectedQuali = blendExpectedQuali(
        params.expectedQuali,
        effectiveFp3,
        w,
      );
      adjusted.qualiStdDev = reduceQualiStdDev(params.qualiStdDev, w);
      break;
    }

    case "after_sprint_quali": {
      // LOCK STAGE for sprint weekends — same importance as FP3 for regular weekends.
      // sprintQuali=null means no time → P20 (NOT a fallback to FP1).
      const effectiveSprintQuali = sprintQuali ?? 20;
      const w = SPRINT_QUALI_WEIGHT * cf;
      adjusted.expectedQuali = blendExpectedQuali(
        params.expectedQuali,
        effectiveSprintQuali,
        w,
      );
      adjusted.qualiStdDev = reduceQualiStdDev(params.qualiStdDev, w);
      // Also reduce sprint variance
      adjusted.sprintDeltaStdDev *= 1 - w * VARIANCE_REDUCTION_FACTOR;
      break;
    }

    case "after_sprint": {
      // Sprint race happened — null means DNF/no finish → P20.
      // Sprint quali is prior context: use actual if available, else use sprint result.
      const effectiveSprint = sprint ?? 20;
      const effectiveSprintQualiForBlend = sprintQuali ?? effectiveSprint;
      const combinedPos =
        effectiveSprintQualiForBlend * 0.6 + effectiveSprint * 0.4;
      const w = (SPRINT_QUALI_WEIGHT + SPRINT_RACE_WEIGHT * 0.3) * cf;
      adjusted.expectedQuali = blendExpectedQuali(
        params.expectedQuali,
        combinedPos,
        w,
      );
      adjusted.qualiStdDev = reduceQualiStdDev(params.qualiStdDev, w);
      break;
    }

    case "after_quali": {
      // Qualifying happened — null means no qualifying time → driver starts from back (P20).
      // This is near-certain data: override with near-zero stdDev.
      // No consistency scaling: actual quali position (or confirmed P20) is definitive.
      const effectiveQuali = quali ?? 20;
      adjusted.expectedQuali = effectiveQuali;
      adjusted.qualiStdDev = QUALI_OVERRIDE_STD_DEV;
      break;
    }
  }

  // Recalculate overtakes using stage-adjusted qualifying position.
  // Without this, a midfield driver with good FP3 (e.g., OCO P6 in FP3)
  // still gets inflated overtakes based on their pre-stage base estimate (~P15).
  // Use a minimum grid factor of 0.5 to prevent ratio explosion when
  // front-row drivers (baseQuali ≈ P2-3) have near-zero grid factors.
  if (adjusted.expectedQuali !== baseQuali) {
    const MIN_GRID_FACTOR = 0.5;
    const oldGridFactor = Math.max(MIN_GRID_FACTOR, (baseQuali - 3) * 0.7);
    const newGridFactor = Math.max(
      MIN_GRID_FACTOR,
      (adjusted.expectedQuali - 3) * 0.7,
    );

    if (adjusted.expectedQuali < baseQuali) {
      // Driver qualifying BETTER than expected (e.g. VER penalty in history but P3 this week).
      // Fewer cars ahead → fewer overtaking opportunities. Scale down.
      adjusted.expectedOvertakes = Math.max(
        0.5,
        Math.min(
          params.expectedOvertakes * (newGridFactor / oldGridFactor),
          15,
        ),
      );
    } else {
      // Driver qualifying WORSE than expected (e.g. ALB historical P15 but sprint quali P18).
      // Live session data confirms the driver/car is genuinely slower this weekend.
      // Do NOT increase overtakes — a slower car can't overtake more. VER at P18 (from
      // a penalty) would overtake plenty, but ALB at P18 (from lack of pace) won't.
      // Keep overtakes at the base estimate or reduce slightly.
      adjusted.expectedOvertakes = params.expectedOvertakes;
    }
  }

  // ---- Fix 1: Dampen race delta based on live session data ----
  const dampingFactor = getDeltaDampingFactor(currentStage);
  if (adjusted.expectedQuali < baseQuali) {
    // Driver qualifying BETTER than expected (e.g. HAM: historical Q8, sprint quali P4).
    // The recovery delta shouldn't fully stack — session data showing better pace means
    // the recovery won't be as large.
    const qualiImprovement = baseQuali - adjusted.expectedQuali;
    adjusted.avgQualiToRaceDelta = Math.max(
      0,
      params.avgQualiToRaceDelta - qualiImprovement * dampingFactor,
    );
  } else if (
    adjusted.expectedQuali > baseQuali &&
    params.avgQualiToRaceDelta > 0
  ) {
    // Driver qualifying WORSE than expected (e.g. ALB: historical P15, sprint quali P18).
    // Live data confirms the car is genuinely slower this weekend. A slow car can't
    // recover as effectively — VER at P18 (penalty) recovers easily, but ALB at P18
    // (lack of pace) fights equally slow cars. Reduce the delta proportionally.
    //
    // However, front-runners having a slight off-day (e.g. LEC base P4.3, session P6)
    // still have genuine pace advantage over the cars around them and WILL recover.
    // The sprint race proves this: LEC qualified P6 but finished P2, overtaking HAM.
    // Scale the penalty by how far back in the grid the driver typically is:
    //   - Front-runners (baseQuali ≤ 5): penalty × 0.3 (keep most recovery potential)
    //   - Mid-grid (baseQuali 6-10): penalty × 0.6
    //   - Backmarkers (baseQuali > 10): full penalty × 1.0
    const qualiDecline = adjusted.expectedQuali - baseQuali;
    const frontRunnerScale = baseQuali <= 5 ? 0.3 : baseQuali <= 10 ? 0.6 : 1.0;
    const declinePenalty = qualiDecline * dampingFactor * frontRunnerScale;
    adjusted.avgQualiToRaceDelta = Math.max(
      0,
      params.avgQualiToRaceDelta - declinePenalty,
    );
  }

  // ---- Fix 2: Hard cap delta so expected race position can't go below P1.5 ----
  // Even after dampening, ensure the math doesn't produce an impossible result.
  // expectedQuali - delta = expectedRacePosition must be >= MIN_EXPECTED_RACE_POSITION.
  const maxRealisticDelta = adjusted.expectedQuali - MIN_EXPECTED_RACE_POSITION;
  if (adjusted.avgQualiToRaceDelta > maxRealisticDelta) {
    adjusted.avgQualiToRaceDelta = Math.max(0, maxRealisticDelta);
  }

  return adjusted;
}
